import React, { useState, useCallback, useEffect } from 'react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertCircle, AlertTriangle, ShieldCheck, Info, Bot } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useBulkEntryState } from './useBulkEntryState';
import BulkEntryRow from './BulkEntryRow';
import LeadReferencePanel from './LeadReferencePanel';

/**
 * BulkPulseEntryGrid - Spreadsheet-style grid for entering pulse data
 * for all sources at once. Assembles the building blocks from Plan 01:
 * - useBulkEntryState (grid state management)
 * - BulkEntryRow (memoized row component)
 * - bulkEntryValidation (shared validation rules)
 * - bulkEntryCalculations (auto-calc formulas)
 */
export default function BulkPulseEntryGrid({
  sources,
  entries,
  selectedGymId,
  selectedPulseId,
  selectedDate,
  autoFields,
  user,
  isPulseActive,
  onSubmitSuccess,
  unverifiedCount,
  blockingCommitments,
  onShowBlockingModal,
  suggestions,
  suggestionsLoading,
  onRefreshSuggestions,
  onConfirmAuto,
}) {
  // Internal state
  const [batchVerified, setBatchVerified] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [fdAvailability, setFdAvailability] = useState('0');
  const [showFDZeroConfirm, setShowFDZeroConfirm] = useState(false);
  const [showVerificationConfirm, setShowVerificationConfirm] = useState(false);

  // Auto-fill state
  const [autoFilledCells, setAutoFilledCells] = useState({});
  // Shape: { [sourceId]: { visit_scheduled: 8, visit_done: 3, closure_expected: 2, revenue_expected: 9000 } }
  const [autoFillMetadata, setAutoFillMetadata] = useState(null);
  // Shape: { synced_at: "...", applied_at: "...", source: 'wtf_api' }

  // Grid state from hook
  const {
    gridData,
    gridErrors,
    updateCell,
    resetGrid,
    filledSourceIds,
    hasAnyErrors,
    submitProgress,
    setSubmitProgress,
  } = useBulkEntryState(sources, entries, selectedGymId, selectedPulseId);

  // Clear auto-fill state when gym or pulse changes
  useEffect(() => {
    setAutoFilledCells({});
    setAutoFillMetadata(null);
  }, [selectedGymId, selectedPulseId]);

  // Count errors in filled rows
  const errorCount = Object.values(gridErrors).reduce(
    (count, rowErrors) => count + Object.keys(rowErrors).length,
    0
  );

  // Count auto-generated unconfirmed entries (filtered by current gym + pulse)
  const autoUnconfirmedEntries = (entries || []).filter(
    e => e.is_auto_generated && !e.auto_confirmed && !e.auto_locked
      && e.gym_id === selectedGymId && e.pulse_id === selectedPulseId
  );
  const autoUnconfirmedCount = autoUnconfirmedEntries.length;

  // Confirm all auto-generated entries
  const handleConfirmAll = async () => {
    if (!onConfirmAuto || autoUnconfirmedEntries.length === 0) return;
    let successCount = 0;
    for (const entry of autoUnconfirmedEntries) {
      try {
        await onConfirmAuto(entry.id || entry._id, {});
        successCount++;
      } catch {
        // onConfirmAuto handles its own toast errors
      }
    }
    if (successCount > 0) {
      toast.success(`Confirmed ${successCount} auto-generated ${successCount === 1 ? 'entry' : 'entries'}`);
    }
  };

  // --- Auto-fill handler ---
  const handleAutoFill = useCallback(() => {
    if (!suggestions || !suggestions.sources || suggestions.sources.length === 0) {
      toast.error('No WTF lead data available for auto-fill');
      return;
    }

    const newAutoFilledCells = {};
    let totalFieldsFilled = 0;

    for (const src of suggestions.sources) {
      const sourceId = src.source_id;
      const sugg = src.suggestions || {};

      // Check if this source exists in the grid
      if (!gridData[sourceId]) {
        continue; // Skip sources not in current grid
      }

      // Only auto-fill empty cells - don't overwrite existing values
      const currentRow = gridData[sourceId];
      const autoValues = {};

      // visit_scheduled
      if (sugg.visit_scheduled && sugg.visit_scheduled > 0 && !currentRow.visit_scheduled) {
        autoValues.visit_scheduled = sugg.visit_scheduled;
        updateCell(sourceId, 'visit_scheduled', String(sugg.visit_scheduled));
        totalFieldsFilled++;
      }

      // visit_done
      if (sugg.visit_done && sugg.visit_done > 0 && !currentRow.visit_done) {
        autoValues.visit_done = sugg.visit_done;
        updateCell(sourceId, 'visit_done', String(sugg.visit_done));
        totalFieldsFilled++;
      }

      // closure_expected
      if (sugg.closure_expected && sugg.closure_expected > 0 && !currentRow.closure_expected) {
        autoValues.closure_expected = sugg.closure_expected;
        updateCell(sourceId, 'closure_expected', String(sugg.closure_expected));
        totalFieldsFilled++;
      }

      // revenue_expected
      if (sugg.revenue_expected && sugg.revenue_expected > 0 && !currentRow.revenue_expected) {
        autoValues.revenue_expected = sugg.revenue_expected;
        updateCell(sourceId, 'revenue_expected', String(sugg.revenue_expected));
        totalFieldsFilled++;
      }

      if (Object.keys(autoValues).length > 0) {
        newAutoFilledCells[sourceId] = autoValues;
      }
    }

    setAutoFilledCells(newAutoFilledCells);
    setAutoFillMetadata({
      synced_at: suggestions.synced_at,
      applied_at: new Date().toISOString(),
      source: 'wtf_api',
    });

    const sourceCount = Object.keys(newAutoFilledCells).length;
    if (totalFieldsFilled > 0) {
      toast.success(
        `Auto-filled ${totalFieldsFilled} field${totalFieldsFilled === 1 ? '' : 's'} across ${sourceCount} source${sourceCount === 1 ? '' : 's'} from verified WTF data`,
        { duration: 4000 }
      );
    } else {
      toast.info('No empty cells to auto-fill. All fields already have values.');
    }
  }, [suggestions, gridData, updateCell]);

  // --- Track cell changes and auto-fill overrides ---
  const handleCellChange = useCallback((sourceId, field, value) => {
    // Check if this cell was auto-filled
    if (autoFilledCells[sourceId] && autoFilledCells[sourceId][field] !== undefined) {
      const originalValue = autoFilledCells[sourceId][field];
      const newValue = field === 'revenue_expected'
        ? (parseFloat(value) || 0)
        : (parseInt(value) || 0);

      // If value changed from auto-filled value, mark as overridden (remove from autoFilledCells)
      if (newValue !== originalValue && value !== '' && value !== String(originalValue)) {
        setAutoFilledCells(prev => {
          const updated = { ...prev };
          if (updated[sourceId]) {
            updated[sourceId] = { ...updated[sourceId] };
            delete updated[sourceId][field];
            // If no more auto-filled fields for this source, remove the source entry
            if (Object.keys(updated[sourceId]).length === 0) {
              delete updated[sourceId];
            }
          }
          return updated;
        });
      }
    }

    // Call original updateCell
    updateCell(sourceId, field, value);
  }, [autoFilledCells, updateCell]);

  // --- Edge case: no gym/pulse selected ---
  if (!selectedGymId || !selectedPulseId) {
    return (
      <div className="text-center py-8 text-gray-500">
        <Info className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">Select a Gym and Pulse above to start bulk entry</p>
      </div>
    );
  }

  // --- Edge case: no sources ---
  if (!sources || sources.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <AlertCircle className="h-8 w-8 mx-auto mb-2 text-gray-400" />
        <p className="text-sm">No active sources configured</p>
      </div>
    );
  }

  // --- Edge case: grid not yet initialized ---
  if (!gridData || Object.keys(gridData).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p className="text-sm">Loading grid...</p>
      </div>
    );
  }

  // --- Submission flow ---
  const handleBulkSubmit = () => {
    // Pre-submit checks
    if (filledSourceIds.length === 0) {
      toast.error('No data entered. Fill at least one source row.');
      return;
    }
    if (hasAnyErrors) {
      toast.error('Please fix validation errors in highlighted rows before submitting.');
      return;
    }
    if (!batchVerified) {
      toast.error('You must verify all entries before submitting. Check the verification checkbox.');
      return;
    }

    // FD=0 confirmation
    const fdValue = parseInt(fdAvailability);
    if (fdValue === 0 || fdAvailability === '0' || fdAvailability === '' || fdAvailability === 0) {
      setShowFDZeroConfirm(true);
      return;
    }

    // Show verification confirmation
    setShowVerificationConfirm(true);
  };

  const handleConfirmedBulkSubmit = async () => {
    setShowVerificationConfirm(false);
    setSubmitting(true);
    setSubmitProgress({ current: 0, total: filledSourceIds.length });

    let successCount = 0;
    let failedRow = null;

    for (const sourceId of filledSourceIds) {
      const rowData = gridData[sourceId];
      const source = sources.find(s => s.source_id === sourceId);

      try {
        if (rowData._existingEntryId) {
          // PATCH existing entry - build only changed fields
          const existingEntry = entries?.find(e =>
            (e.id === rowData._existingEntryId || e._id === rowData._existingEntryId)
          );

          const patchBody = {};
          const fieldsToCompare = [
            'dialed', 'dialed_company_ivr', 'dialed_fd_phone', 'connected',
            'visit_scheduled', 'visit_done', 'closure_expected', 'closure_phone', 'closure_counter'
          ];

          // Fields that cannot be decreased (activity counts only go up)
          const noDecreaseFields = new Set([
            'dialed', 'connected', 'visit_scheduled', 'visit_done', 'closure_phone', 'closure_counter'
          ]);

          for (const field of fieldsToCompare) {
            const newVal = parseInt(rowData[field]) || 0;
            const oldVal = existingEntry ? (parseInt(existingEntry[field]) || 0) : 0;
            if (newVal !== oldVal) {
              // Block value decreases on protected fields
              if (noDecreaseFields.has(field) && newVal < oldVal && user?.role !== 'admin') {
                failedRow = {
                  sourceId,
                  sourceName: source?.name || sourceId,
                  error: `Cannot reduce ${field} from ${oldVal} to ${newVal}. Values can only increase.`,
                };
                break;
              }
              patchBody[field] = newVal;
            }
          }

          // If a decrease was detected, stop processing this row
          if (failedRow) break;

          // Revenue (float comparison)
          const newRev = parseFloat(rowData.revenue_expected) || 0;
          const oldRev = existingEntry ? (parseFloat(existingEntry.revenue_expected) || 0) : 0;
          if (newRev !== oldRev) {
            patchBody.revenue_expected = newRev;
          }

          // FD availability
          const newFd = parseInt(fdAvailability) || 0;
          const oldFd = existingEntry ? (parseInt(existingEntry.fd_availability) || 0) : 0;
          if (newFd !== oldFd) {
            patchBody.fd_availability = newFd;
          }

          // Comments
          if ((rowData.comments || '') !== (existingEntry?.comments || '')) {
            patchBody.comments = rowData.comments || '';
          }

          // Only PATCH if there are actual changes
          if (Object.keys(patchBody).length > 0) {
            patchBody.edit_reason = 'Bulk entry update';
            await axiosInstance.patch(`/pulse-entries/${rowData._existingEntryId}/edit`, patchBody);
          }
        } else {
          // POST new entry
          const postBody = {
            entry_date: selectedDate,
            gym_id: selectedGymId,
            pulse_id: selectedPulseId,
            source_id: sourceId,
            dialed: parseInt(rowData.dialed) || 0,
            dialed_company_ivr: parseInt(rowData.dialed_company_ivr) || 0,
            dialed_fd_phone: parseInt(rowData.dialed_fd_phone) || 0,
            connected: parseInt(rowData.connected) || 0,
            visit_scheduled: parseInt(rowData.visit_scheduled) || 0,
            visit_done: parseInt(rowData.visit_done) || 0,
            closure_expected: parseInt(rowData.closure_expected) || 0,
            revenue_expected: parseFloat(rowData.revenue_expected) || 0.0,
            closure_phone: parseInt(rowData.closure_phone) || 0,
            closure_counter: parseInt(rowData.closure_counter) || 0,
            fd_availability: parseInt(fdAvailability) || 0,
            comments: rowData.comments || '',
            tl_verified: batchVerified,
            // Auto-populated fields
            gym_name: autoFields.gym_name,
            tl_id: autoFields.tl_id,
            tl_name: autoFields.tl_name,
            manager_id: autoFields.manager_id,
            manager_name: autoFields.manager_name,
          };

          // Add auto-fill metadata if this entry was auto-filled
          if (autoFillMetadata && autoFilledCells[sourceId]) {
            postBody.auto_fill_source = 'wtf_api';
            postBody.auto_fill_values = autoFilledCells[sourceId];
            postBody.wtf_data_synced_at = autoFillMetadata.synced_at;
            postBody.auto_fill_applied_at = autoFillMetadata.applied_at;
          }

          await axiosInstance.post('/pulse-entries', postBody);
        }

        successCount++;
        setSubmitProgress(prev => ({ ...prev, current: successCount }));
      } catch (error) {
        failedRow = {
          sourceId,
          sourceName: source?.name || sourceId,
          error: error.response?.data?.detail || error.message || 'Unknown error',
        };
        break; // Stop on first failure
      }
    }

    setSubmitting(false);

    if (!failedRow) {
      toast.success(`${successCount} ${successCount === 1 ? 'entry' : 'entries'} submitted successfully!`);
      setBatchVerified(false);
      setFdAvailability('0');
      setAutoFilledCells({});
      setAutoFillMetadata(null);
      resetGrid();
      onSubmitSuccess?.();
    } else {
      toast.error(
        `Failed at ${failedRow.sourceName}: ${failedRow.error}. ${successCount}/${filledSourceIds.length} entries submitted.`
      );
      onSubmitSuccess?.(); // Refresh to show partial results
    }
  };

  // Determine submit button state and text
  const isSubmitDisabled = submitting ||
    hasAnyErrors ||
    filledSourceIds.length === 0 ||
    !isPulseActive(selectedPulseId) ||
    unverifiedCount > 0 ||
    blockingCommitments.length > 0 ||
    !batchVerified;

  const getSubmitButtonText = () => {
    if (submitting) {
      return `Submitting ${submitProgress.current}/${submitProgress.total}...`;
    }
    if (!isPulseActive(selectedPulseId)) {
      return `${selectedPulseId} window closed`;
    }
    if (blockingCommitments.length > 0) {
      return `Resolve ${blockingCommitments.length} Commitment${blockingCommitments.length === 1 ? '' : 's'}`;
    }
    if (unverifiedCount > 0) {
      return `${unverifiedCount} Unverified ${unverifiedCount === 1 ? 'Entry' : 'Entries'}`;
    }
    if (!batchVerified) {
      return 'Please verify data first';
    }
    if (filledSourceIds.length === 0) {
      return 'No data entered';
    }
    return `Submit All (${filledSourceIds.length} ${filledSourceIds.length === 1 ? 'entry' : 'entries'})`;
  };

  return (
    <div>
      {/* Mobile notice */}
      <div className="block sm:hidden mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          For best experience, use Bulk Entry on desktop. Switch to Single Entry for mobile.
        </p>
      </div>

      {/* Unverified count banner */}
      {unverifiedCount > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-800">
              You have <strong>{unverifiedCount}</strong> unverified pulse {unverifiedCount === 1 ? 'entry' : 'entries'}.
              Please verify all entries before submitting new pulses.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 border-amber-500 text-amber-700 hover:bg-amber-100"
              onClick={() => window.location.href = '/verification'}
            >
              Go to Verification
            </Button>
          </div>
        </div>
      )}

      {/* Blocking commitments banner */}
      {blockingCommitments.length > 0 && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-300 rounded-lg flex items-start gap-2">
          <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div>
            <p className="text-sm text-amber-800">
              You have <strong>{blockingCommitments.length}</strong> unresolved commitment{blockingCommitments.length === 1 ? '' : 's'}.
            </p>
            <Button
              variant="outline"
              size="sm"
              className="mt-2 border-amber-500 text-amber-700 hover:bg-amber-100"
              onClick={onShowBlockingModal}
            >
              Resolve Commitments
            </Button>
          </div>
        </div>
      )}

      {/* Auto-generated entries banner */}
      {autoUnconfirmedCount > 0 && (
        <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg mb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bot size={18} className="text-blue-400" />
              <span className="text-sm text-blue-300 font-medium">
                {autoUnconfirmedCount} auto-generated {autoUnconfirmedCount === 1 ? 'entry' : 'entries'} ready for review
              </span>
            </div>
            <button
              onClick={handleConfirmAll}
              className="px-3 py-1 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-md transition-colors"
            >
              Confirm All
            </button>
          </div>
        </div>
      )}

      {/* WTF Lead Reference Panel */}
      <LeadReferencePanel
        suggestions={suggestions}
        loading={suggestionsLoading}
        onAutoFill={handleAutoFill}
        onRefresh={onRefreshSuggestions}
      />

      {/* Shared FD Availability field */}
      <div className="flex items-center gap-3 mb-4">
        <Label className="text-sm font-medium whitespace-nowrap">FD Availability</Label>
        <Input
          type="number"
          min="0"
          step="1"
          className="h-9 w-20"
          value={fdAvailability}
          onChange={(e) => setFdAvailability(e.target.value)}
        />
        <span className="text-xs text-gray-500">Applies to all entries for this gym</span>
      </div>

      {/* Grid table (hidden on mobile) */}
      <div className="hidden sm:block overflow-x-auto border rounded-lg shadow-sm">
        <table className="w-full border-collapse text-xs">
          <thead className="sticky top-0 z-20 bg-gray-100">
            <tr>
              {/* Source - sticky */}
              <th className="sticky left-0 z-30 bg-gray-100 border-r-2 border-gray-300 px-3 py-2 text-left font-semibold text-sm whitespace-nowrap">
                Source
              </th>
              {/* Calls group */}
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">Dialed</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">Company IVR</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">FD Phone</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap border-l-2 border-gray-200">Connected</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap bg-gray-50">Connect %</th>
              {/* Visits group */}
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap border-l-2 border-gray-200">Visit Scheduled</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">Visit Done</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap bg-gray-50">Schedule %</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap bg-gray-50">Done %</th>
              {/* Closures group */}
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap border-l-2 border-gray-200">Closure Expected</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">Revenue Expected</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">Closure Phone</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap">Closure Counter</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap bg-gray-50">Total Closure</th>
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap bg-gray-50">Closure Rate %</th>
              {/* Other */}
              <th className="px-1 py-2 text-center font-medium whitespace-nowrap border-l-2 border-gray-200">Comments</th>
            </tr>
          </thead>
          <tbody>
            {sources.map(source => {
              // Find matching existing entry for auto-generation state
              const matchingEntry = (entries || []).find(
                e => e.source_id === source.source_id
                  && e.gym_id === selectedGymId
                  && e.pulse_id === selectedPulseId
              );
              return (
                <BulkEntryRow
                  key={source.source_id}
                  sourceId={source.source_id}
                  sourceName={source.name}
                  sourceNeedsVisit={source.needs_visit === 'Yes'}
                  rowData={gridData[source.source_id]}
                  errors={gridErrors[source.source_id]}
                  onCellChange={handleCellChange}
                  autoFilledFields={autoFilledCells[source.source_id] || {}}
                  isAutoGenerated={matchingEntry?.is_auto_generated || false}
                  autoConfirmed={matchingEntry?.auto_confirmed || false}
                  autoLocked={matchingEntry?.auto_locked || false}
                  graceWindowExpiresAt={matchingEntry?.grace_window_expires_at}
                  onConfirmEntry={onConfirmAuto}
                  entryId={matchingEntry?.id || matchingEntry?._id}
                  autoFulfilled={matchingEntry?.auto_fulfilled || false}
                  autoFulfilledNeedsReview={matchingEntry?.auto_fulfilled_needs_review || false}
                />
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Summary bar */}
      <div className="flex items-center justify-between mt-3 px-1">
        <span className="text-sm text-gray-600">
          {filledSourceIds.length} of {sources.length} sources filled
        </span>
        {hasAnyErrors && (
          <span className="text-sm text-red-600 font-medium">
            {errorCount} {errorCount === 1 ? 'error' : 'errors'} - Fix errors in highlighted rows before submitting
          </span>
        )}
      </div>

      {/* Pulse timing warning */}
      {!isPulseActive(selectedPulseId) && selectedPulseId && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
          <div className="text-red-700 text-sm font-medium flex items-center gap-2">
            <AlertCircle size={16} />
            Submission window closed for {selectedPulseId}
          </div>
        </div>
      )}

      {/* Batch verification checkbox */}
      <div className="mt-4">
        <div className={`flex items-center space-x-2 p-3 rounded-lg border-2 transition-colors ${
          batchVerified
            ? 'bg-green-50 border-green-400'
            : 'bg-red-50 border-red-300'
        }`}>
          <Checkbox
            id="bulk-tl-verified"
            checked={batchVerified}
            onCheckedChange={(checked) => setBatchVerified(checked)}
            className="h-5 w-5"
          />
          <Label htmlFor="bulk-tl-verified" className="text-sm font-medium cursor-pointer flex items-center gap-2">
            {batchVerified ? (
              <>
                <ShieldCheck size={16} className="text-green-600" />
                <span className="text-green-700">Data Verified for all {filledSourceIds.length} {filledSourceIds.length === 1 ? 'entry' : 'entries'}</span>
              </>
            ) : (
              <>
                <AlertCircle size={16} className="text-red-500" />
                <span className="text-red-600">
                  MANDATORY: Verify data for all {filledSourceIds.length > 0 ? filledSourceIds.length : ''} {filledSourceIds.length === 1 ? 'entry' : 'entries'} before submitting
                </span>
              </>
            )}
          </Label>
        </div>
      </div>

      {/* Submit All button */}
      <Button
        className={`w-full h-10 mt-4 ${
          !batchVerified || unverifiedCount > 0 || blockingCommitments.length > 0
            ? 'bg-gray-400 hover:bg-gray-500'
            : 'bg-[#1F4E79] hover:bg-[#163a5c]'
        }`}
        disabled={isSubmitDisabled}
        onClick={(e) => {
          if (blockingCommitments.length > 0) {
            e.preventDefault();
            onShowBlockingModal?.();
            return;
          }
          handleBulkSubmit();
        }}
      >
        {getSubmitButtonText()}
      </Button>

      {/* FD Zero confirm dialog */}
      <AlertDialog open={showFDZeroConfirm} onOpenChange={setShowFDZeroConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg">Zero Front Desk Availability</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-left space-y-3">
              <p className="text-gray-700">
                Are you sure you have zero front desk available today? This is unusual and may indicate a data entry error.
              </p>
              <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 text-sm">
                <p className="text-amber-800 font-medium">
                  FD Availability is set to 0 for {autoFields?.gym_name || 'this gym'}.
                </p>
                <p className="text-amber-700 mt-1">
                  If this is correct, confirm below to proceed with submission.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="flex-1">Go Back & Edit</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setShowFDZeroConfirm(false);
                setShowVerificationConfirm(true);
              }}
              className="flex-1 bg-amber-600 hover:bg-amber-700"
            >
              Yes, FD is Zero
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Verification confirm dialog */}
      <AlertDialog open={showVerificationConfirm} onOpenChange={setShowVerificationConfirm}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <ShieldCheck className="h-6 w-6 text-amber-600" />
              </div>
              <AlertDialogTitle className="text-lg">Confirm Bulk Submission</AlertDialogTitle>
            </div>
            <AlertDialogDescription className="text-left space-y-3">
              <p className="text-gray-700 font-medium">
                Submit {filledSourceIds.length} {filledSourceIds.length === 1 ? 'entry' : 'entries'} for {autoFields?.gym_name || 'this gym'}?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg text-sm space-y-1">
                <p><span className="text-gray-500">Gym:</span> <span className="font-medium">{autoFields?.gym_name}</span></p>
                <p><span className="text-gray-500">Pulse:</span> <span className="font-medium">{selectedPulseId}</span></p>
                <p><span className="text-gray-500">Entries:</span> <span className="font-medium">{filledSourceIds.length} source{filledSourceIds.length === 1 ? '' : 's'}</span></p>
                <p><span className="text-gray-500">FD Availability:</span> <span className="font-medium">{fdAvailability}</span></p>
              </div>
              <p className="text-amber-700 text-sm bg-amber-50 p-2 rounded border border-amber-200">
                By clicking "Yes, Submit All", you confirm that all data entered is accurate and has been verified.
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel className="flex-1">Go Back & Review</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmedBulkSubmit}
              className="flex-1 bg-[#1F4E79] hover:bg-[#163a5c]"
            >
              Yes, Submit All
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
